import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUnreadRefresh } from '@/components/Layout';
import api from '@/lib/api';
import { useAttachmentImages } from '@/hooks/useAttachmentImages';
import { AttachmentImage } from '@/components/AttachmentImage';
import { Button } from '@/components/ui/button';
import { Send, ChevronDown, ChevronUp, Lock, Smile, Paperclip, X, Trash2, ImageIcon, RefreshCw, Sparkles, AlertTriangle, Copy, Check, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import { mentionQueryAt, insertMentionAt } from '@/lib/mentions';
import { ROLE_COLORS, roleInk, AVATAR_WASH_HEX } from '@/lib/roleInk';
import { useIsLight } from '@/lib/useIsLight';
import { draftKey, readDraft, writeDraft, boxHeight, popOutPlace, GROW_MAX } from '@/lib/chatBox';

const MAX_MSG_LEN = 10000;
const SOFT_WARN_AT = 8000;
const MAX_IMG_BYTES = 20 * 1024 * 1024; // 20 MB — matches backend
const ACCEPTED_IMG = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
const ACCEPT_ATTR = ACCEPTED_IMG.join(',');

// Returns true if an @{name} mention token for this user actually appears in `text`.
// Full-token match with a trailing non-alphanumeric boundary so "@Ann" doesn't match
// inside "@Anna". Mirrors insertMention()'s "@{u.name}" insertion and the backend safety net.
const mentionInText = (name, text) => {
  if (!name || !text) return false;
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp('@' + esc + '(?![A-Za-z0-9])', 'i').test(text);
};

// ── Lightbox: fullscreen image viewer ──
function Lightbox({ src, alt, onClose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div
      data-testid="chat-attachment-lightbox"
      className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
      onClick={onClose}>
      <button
        data-testid="chat-attachment-lightbox-close"
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
        <X className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt={alt || ''}
        className="max-w-[95vw] max-h-[95vh] object-contain rounded-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
}

const EMOJI_GROUPS = {
  'Smileys': ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','😍','🥰','😘','😗','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','😐','😑','😶','😏','😒','🙄','😬','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😢','😭','😱','😖','😣','😞','😓','😩','😤','🤬','😡','😠','🤯','😳','🥵','🥶','😎','🤠','🥳','😺','👍','👎','👏','🙌','🤝','🙏','💪','❤️','🔥','💯','⭐','✨'],
  'Nature': ['🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🌸','🌹','🌻','🌺','🌲','🌴','🍀','🌈','☀️','🌙','⭐','🌊'],
  'Food': ['🍎','🍕','🍔','🍟','🌮','🍣','🍜','🍝','🍩','🍰','🎂','🍫','☕','🍺','🍷','🥂','🍹'],
  'Objects': ['📷','🎬','🎥','🎵','🎶','🎤','🎧','💻','📱','✉️','📌','📎','✂️','📝','💡','🔔','📅','💰','🏆','🎯','🔑'],
  'Symbols': ['✅','❌','⚠️','💬','💭','👁️','🔴','🟢','🟡','⬆️','⬇️','➡️','⬅️','↩️','🔄','➕','➖','✖️','❓','❗','💲'],
};

// THE reaction set. MIRROR of CHAT_REACTIONS in backend/server.py — keep in sync;
// the server rejects anything not on its own list, so a drift here surfaces as a
// 400 rather than a silently-missing reaction.
//
// NO CAPTIONS. An earlier version listed a label beside each emoji ("👍 Sounds
// good"). Operator removed it: a reaction means what the sender meant, and
// captioning it is the product telling people how to read a symbol they already
// understand. Ten is still deliberate — a free-form picker makes "3 people
// reacted 👍" unreadable. No 👎: on creative work it tells an editor nothing
// actionable, and the revision-request flow already carries "no".
export const CHAT_REACTIONS = ['👍', '❤️', '🔥', '🎉', '😂', '🙏', '✅', '👀', '❓', '⏳'];

// Existing reactions, grouped by emoji with a count, plus an add button.
// Hovering a pill names who reacted and what it meant — the label is the whole
// point of the feature, so it must be reachable without asking anyone.
// `open` is OWNED BY ChatThread, not by this component. MessageBubble is defined
// inside ChatThread's body, so every ChatThread render creates a new component
// type and React unmounts/remounts the whole message list — destroying any state
// held down here. That happens on the 15s poll AND on every keystroke in the
// composer, so a picker opened here vanished on its own within seconds
// ("finicky... sometimes it disappears while I'm looking at it"). Parent state
// survives those renders; this is the targeted fix, short of restructuring
// MessageBubble out of the closure it depends on.
function ReactionRow({ msg, mine, onToggle, readOnly, open, anchorRect, onOpenChange }) {
  const ref = React.useRef(null);
  const btnRef = React.useRef(null);
  const pickerRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    // The picker is PORTALLED to <body>, so it is not inside `ref` and a naive
    // "outside" test would treat clicking a reaction as an outside click: mousedown
    // closes the picker, it unmounts, and the click never lands on the button — the
    // selection silently does nothing. Both nodes have to count as inside.
    const h = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPicker = pickerRef.current && pickerRef.current.contains(e.target);
      if (!inTrigger && !inPicker) onOpenChange(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onOpenChange]);

  const groups = [];
  for (const r of (msg.reactions || [])) {
    let g = groups.find(x => x.emoji === r.emoji);
    if (!g) groups.push(g = { emoji: r.emoji, people: [] });
    g.people.push(r);
  }
  if (readOnly && groups.length === 0) return null;

  return (
    <div className="flex items-center gap-1 mt-1 flex-wrap" data-testid={`reactions-${msg.id}`}>
      {groups.map(g => {
        const isMine = g.people.some(p => p.user_id === mine?.id);
        return (
          <button key={g.emoji} type="button" disabled={readOnly}
            onClick={() => onToggle(msg, g.emoji)}
            data-testid={`reaction-${msg.id}-${g.emoji}`}
            title={g.people.map(p => p.user_name).join(', ')}
            className={`tap-auto inline-flex items-center gap-1 h-5 px-1.5 rounded-full border text-[10px] transition-colors ${
              isMine ? 'border-ug-gold/40 bg-ug-gold/10 text-ug-gold' : 'border-ug-border bg-white/[0.03] text-slate-400 hover:border-gold-leaf/30'}`}>
            <span>{g.emoji}</span>
            {g.people.length > 1 && <span className="font-mono">{g.people.length}</span>}
          </button>
        );
      })}
      {!readOnly && (
        <div ref={ref}>
          {/* ALWAYS VISIBLE, just quiet. This was `opacity-0 group-hover:opacity-100`,
              which needs a Tailwind `group` ancestor — there isn't one here, so the
              button sat at opacity 0 permanently and nobody could find it. It still
              "worked" under a scripted click, which is exactly how that shipped. */}
          <button type="button" ref={btnRef}
            onClick={() => open
              ? onOpenChange(false)
              : onOpenChange(true, btnRef.current.getBoundingClientRect())}
            data-testid={`reaction-add-${msg.id}`} title="Add a reaction"
            className="tap-auto inline-flex items-center justify-center h-5 w-5 rounded-full border border-ug-border text-slate-500 opacity-50 hover:opacity-100 hover:text-ug-gold hover:border-gold-leaf/30 transition-all">
            <Smile className="w-3 h-3" />
          </button>
          {/* PORTALLED to <body>, not absolutely positioned in place. The message
              list is a scrolling overflow container, so an in-flow dropdown gets
              CLIPPED to a thin vertical strip — the emoji stack up in a sliver and
              the labels, which are the entire point of this picker, are cut off
              completely. Same fix the compose-emoji picker in this file already
              uses. Flips above/below the button so it is never off-screen. */}
          {open && anchorRect && ReactDOM.createPortal(
            (() => {
              const r = anchorRect;
              const H = 110;                       // approx height (2 rows of 5)
              const above = r.top > H + 12;        // room above? otherwise drop down
              return (
                <div ref={pickerRef}
                     style={{
                       position: 'fixed', zIndex: 9999,
                       left: `${Math.max(8, Math.min(r.left, window.innerWidth - 236))}px`,
                       ...(above ? { bottom: `${window.innerHeight - r.top + 6}px` }
                                 : { top: `${r.bottom + 6}px` }),
                     }}
                     className="w-[228px] p-1.5 rounded-lg bg-ug-raised border border-ug-border-strong shadow-2xl grid grid-cols-5 gap-0.5"
                     data-testid={`reaction-picker-${msg.id}`}>
                  {CHAT_REACTIONS.map(x => (
                    <button key={x} type="button"
                      onClick={() => { onToggle(msg, x); onOpenChange(false); }}
                      data-testid={`reaction-pick-${x}`}
                      className="tap-auto h-9 w-full flex items-center justify-center rounded hover:bg-white/[0.08] text-[18px] leading-none transition-colors">
                      {x}
                    </button>
                  ))}
                </div>
              );
            })(),
            document.body
          )}
        </div>
      )}
    </div>
  );
}

function EmojiGrid({ onSelect, onClose }) {
  const ref = React.useRef(null);
  const [tab, setTab] = React.useState('Smileys');
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  const tabs = Object.keys(EMOJI_GROUPS);
  return (
    <div ref={ref} className="w-[280px] bg-ug-raised border border-ug-border-strong rounded-lg shadow-2xl overflow-hidden" data-testid="emoji-picker">
      <div className="flex border-b border-ug-border px-1 py-1 gap-0.5 overflow-x-auto">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider whitespace-nowrap tap-sm transition-colors ${tab === t ? 'bg-gold-leaf/15 text-ug-gold' : 'text-slate-500 hover:text-slate-300'}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="h-[200px] overflow-y-auto p-2">
        <div className="grid grid-cols-8 gap-0.5">
          {EMOJI_GROUPS[tab].map((emoji, i) => (
            <button key={i} onClick={() => onSelect(emoji)} className="w-8 h-8 flex items-center justify-center rounded hover:bg-white/10 text-lg tap-sm transition-colors">
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Author-role identity colours: ONE list and ONE rule for their text step, in lib/roleInk.js.

/**
 * ChatThread — uses structurally separated chat systems.
 * @param {string} projectId
 * @param {"client"|"internal"} system — which chat system to use
 * @param {boolean} compact — collapsible compact mode
 * @param {boolean} readOnly — hide input when true
 */
export default function ChatThread({ projectId, system = 'client', compact = false, readOnly = false, defaultExpanded = false, onRead, clientName, pmOnDuty = null }) {
  const { user } = useAuth();
  // author names and initials are TEXT in an identity hue; the hue's text step depends on the theme (lib/roleInk.js)
  const isLight = useIsLight();
  const [messages, setMessages] = useState([]);
  // Which message's reaction picker is open. Lives HERE so it survives the
  // remount-the-world renders described on ReactionRow. Null = none open.
  const [picker, setPicker] = useState(null);   // { id, rect } | null

  /* Attachment images for the whole thread, through the SAME state machine the
     file-request panels use. Transport is 'direct' here: chat attachments 302 to
     R2, so a CORS fetch would follow the redirect cross-origin, send `Origin: null`
     and be refused — the blob transport is not available without a server change.
     A direct <img> cannot read a status code, so chat never shows `gone`; every
     failure is `failed` with a retry offered.

     This replaces `failedAttachments`, which was written once and never cleared:
     one transient error and the image read "(unavailable)" until the component
     remounted, with no retry. Aggregated at thread level so a poll returning the
     same attachments produces an identical id string and does not re-fire the
     ladder. */
  const allAttachments = useMemo(
    () => messages.flatMap(m => (Array.isArray(m.attachments) ? m.attachments : [])),
    [messages]);
  const { imageState, retryImage, reportLoaded, reportError } = useAttachmentImages(
    allAttachments,
    { transport: 'direct', endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/storage/serve` });
  // the unsent words are kept per thread for the tab, and the box can open in a big window of its own (lib/chatBox.js)
  const threadDraftKey = draftKey(projectId, system);
  const [newMsg, setNewMsg] = useState(() => readDraft(threadDraftKey));
  const draftKeyRef = useRef(threadDraftKey);
  const [bigBox, setBigBox] = useState(false);
  const [viewW, setViewW] = useState(() => window.innerWidth);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded || !compact);
  const refreshUnreadCounts = useUnreadRefresh();  // Sync expanded when defaultExpanded changes (e.g., from morning brief chat click)
  React.useEffect(() => { if (defaultExpanded) setExpanded(true); }, [defaultExpanded]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [taggableUsers, setTaggableUsers] = useState([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [pendingMentions, setPendingMentions] = useState([]);
  const [urgent, setUrgent] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [failedPhotos, setFailedPhotos] = useState({});
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{tempId, file_id?, filename, previewUrl, file, uploading, error?}]
  const [dragOver, setDragOver] = useState(false);
  const [lightboxFid, setLightboxFid] = useState(null);
  const [deletingMsgId, setDeletingMsgId] = useState(null);
  const scrollRef = useRef(null);
  const emojiRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastSeenMessageIdRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const userJustSentRef = useRef(false);
  const prevExpandedRef = useRef(expanded);

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el || bigBox) return;   // the big window's box fills its window
    el.style.height = 'auto';
    el.style.height = boxHeight(el.scrollHeight) + 'px';
  };

  // Pin the scroll container to the bottom AFTER layout settles. Double rAF defers
  // the measurement until the freshly-mounted/expanded list (avatars, attachments,
  // multi-line bubbles) has been laid out — this is the root-cause fix for the chat
  // opening at the top instead of the newest message.
  //
  // Stick to the newest message (Ryan, 2026-10-05: "Open Chat in Portal" from an email landed toward the top of the
  // chat). That one-off pin runs a frame after the messages render, but on a fresh page every photo then loads and
  // grows: the 4:3 placeholder becomes the real photo, a portrait or a phone screenshot 100-300 px taller, and nothing
  // followed it, so a chat with a few photos opened far above its latest message. stickRef is true while the view is
  // at the newest message; anything that grows the list then keeps it there. Scrolling up to read turns it off, so a
  // reader looking at older messages is never pulled back down.
  const stickRef = useRef(true);
  const scrollToBottom = () => {
    stickRef.current = true;
    listGapRef.current = 0;   // the intent is the bottom; a gap measured before the pin must not undo it
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    });
  };

  // Ryan, 2026-09-30: opening the suggested reply (or anything else that makes the box under the chat taller:
  // steps, attachments, a long message) must not push the message box down the drawer. The box stays where it
  // was and the message list gives up the height instead, keeping its newest line in view.
  //   composerGrow: how much taller the box under the list is than the shortest it has been
  //   the list observer: when the list changes height, keep the same distance between the bottom of the view and the
  //     newest message. Stored as that distance, not as scrollTop: when the list grows back the browser has already
  //     moved scrollTop itself, and adding the difference on top of that left the view 191px short of the end.
  const composerRef = useRef(null);
  const listGapRef = useRef(0);
  const listGap = (el) => el.scrollHeight - el.scrollTop - el.clientHeight;
  const composerRestRef = useRef(null);
  const [composerGrow, setComposerGrow] = useState(0);
  useEffect(() => {
    const el = composerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const h = el.offsetHeight;
      if (composerRestRef.current === null || h < composerRestRef.current) composerRestRef.current = h;
      setComposerGrow(h - composerRestRef.current);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, readOnly]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let prev = el.clientHeight;
    listGapRef.current = listGap(el);
    const onScroll = () => { listGapRef.current = listGap(el); stickRef.current = listGapRef.current <= 40; };
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      if (h !== prev) el.scrollTop = el.scrollHeight - h - listGapRef.current;
      prev = h;
    });
    ro.observe(el);
    // the messages themselves growing (a photo loading, a preview, a font): follow it while stuck to the bottom
    const grow = new ResizeObserver(() => { if (stickRef.current) el.scrollTop = el.scrollHeight; });
    [...el.children].forEach((c) => grow.observe(c));
    return () => { ro.disconnect(); grow.disconnect(); el.removeEventListener('scroll', onScroll); };
  }, [expanded]);

  const basePath = system === 'internal'
    ? `/chat/internal/${projectId}/messages`
    : `/chat/client/${projectId}/messages`;

  useEffect(() => { autoResize(); }, [newMsg, bigBox]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (draftKeyRef.current === threadDraftKey) return;
    draftKeyRef.current = threadDraftKey;
    setNewMsg(readDraft(threadDraftKey));
  }, [threadDraftKey]);
  useEffect(() => { writeDraft(draftKeyRef.current, newMsg); }, [newMsg]);
  // a staff request, 2026-11-03: "PLEASE allow me to make this text box bigger especially when editing large messages";
  // Ryan the same day: a big window beside the drawer. Opening or closing it puts the caret at the end of the words in
  // the box now on screen (never on first render: a drawer opening takes no focus); the window follows the screen's width.
  const bigBoxRef = useRef(false);
  useEffect(() => {
    if (bigBoxRef.current === bigBox) return;
    bigBoxRef.current = bigBox;
    const el = textareaRef.current;
    if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
  }, [bigBox]);
  useEffect(() => {
    if (!bigBox) return undefined;
    const onResize = () => setViewW(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bigBox]);
  const sizeButton = (
    <button type="button" data-testid={`chat-box-size-${system}`} title="Open a bigger box" aria-label="Open a bigger box"
      onClick={() => setBigBox(true)}
      className="tap-auto absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded text-slate-500 hover:text-ug-gold transition-colors">
      <Maximize2 className="w-3.5 h-3.5" />
    </button>
  );
  // while the big window is open, the small box says so and takes you back to it
  const bigBoxNote = (
    <button type="button" data-testid={`chat-box-in-window-${system}`} onClick={() => textareaRef.current?.focus()}
      className="tap-auto w-full h-9 px-3 rounded-md border border-dashed border-ug-border text-left text-[11px] text-slate-500 hover:text-ug-gold transition-colors truncate">
      Writing in the bigger box
    </button>
  );

  useEffect(() => {
    if (!projectId) return;
    isInitialLoadRef.current = true;
    lastSeenMessageIdRef.current = null;
    api.get(basePath).then(r => setMessages(r.data)).catch(() => setMessages([]));
    api.get(`/users/taggable?project_id=${projectId}&system=${system}`).then(r => setTaggableUsers(r.data)).catch(() => {});
    // Fetch unread status
    api.get('/chat/my-read-status').then(r => {
      const ids = r.data[system] || [];
      setUnreadCount(ids.includes(projectId) ? -1 : 0); // -1 = has unread (exact count unknown)
    }).catch(() => {});
    const interval = setInterval(() => {
      api.get(basePath).then(r => setMessages(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [projectId, basePath, system]);

  // Compute unread: messages from "other party" since we don't have exact last_read_at on frontend,
  // use the unreadCount flag from read-status (-1 = unread, 0 = read)
  useEffect(() => {
    if (unreadCount === -1 && messages.length > 0) {
      // Count messages from other roles at the end of the list
      let count = 0;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].author_role === user.role) break;
        count++;
      }
      setUnreadCount(count || 1);
    }
  }, [messages, unreadCount, user.role]);

  // Mark as read when expanded — UNCONDITIONALLY, once per expansion.
  // The old `unreadCount > 0` gate skipped the PUT whenever read-status said
  // the thread was "read" — but read-status is COUNTERPARTY math, and a
  // same-side @mention (one admin tagging another) leaves unreadCount at 0.
  // The read cursor then never advanced on open, so the brief's Mentions card
  // only cleared when the viewer REPLIED (the send endpoint auto-marks read) —
  // observed in the wild as "I have to tag them back to make it go away".
  const markedReadThisExpansionRef = useRef(false);
  useEffect(() => {
    if (!expanded) { markedReadThisExpansionRef.current = false; return; }
    if (!projectId || markedReadThisExpansionRef.current) return;
    markedReadThisExpansionRef.current = true;
    api.put(`/chat/${system}/${projectId}/read`).then(() => {
      setUnreadCount(c => (c > 0 ? 0 : c));
      if (onRead) onRead();
      refreshUnreadCounts();
    }).catch(() => {});
  }, [expanded, projectId, system]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pin to bottom ONLY for: initial load, expansion (false→true), and after the user sends.
  // Live incoming messages never yank a user who has scrolled up to read older messages.
  useEffect(() => {
    if (!expanded || messages.length === 0) { prevExpandedRef.current = expanded; return; }
    const newestId = messages[messages.length - 1].id;
    const justExpanded = expanded && !prevExpandedRef.current;

    if (isInitialLoadRef.current || justExpanded || userJustSentRef.current) {
      scrollToBottom();
      isInitialLoadRef.current = false;
      userJustSentRef.current = false;
    }
    prevExpandedRef.current = expanded;
    if (newestId) lastSeenMessageIdRef.current = newestId;
    if (scrollRef.current && !stickRef.current) listGapRef.current = listGap(scrollRef.current);  // a new message moves the end
  }, [messages, expanded]);



  // Registry of in-flight upload AbortControllers, keyed by tempId.
  // Each entry: { controller, userAborted }. `userAborted` is the SINGLE reason
  // flag that distinguishes a user X-click (silent removal, no toast) from a
  // timeout/error (visible failed state + toast) — one abort mechanism, two reasons.
  const uploadControllersRef = useRef({});

  // ── Upload one image: validate → presign → R2 PUT → confirm-upload ──
  // Split timeouts via a shared AbortController: 20s presign, 120s R2 PUT, 20s confirm.
  // On timeout/error the attachment flips to a VISIBLE failed state (kept, not removed)
  // with a Retry affordance. `existingTempId` is passed on retry to reuse the tile+preview.
  const uploadAttachment = async (file, existingTempId = null, existingPreviewUrl = null) => {
    if (!existingTempId) {
      if (!ACCEPTED_IMG.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.type || 'unknown'}. Allowed: PNG, JPEG, WebP, GIF.`);
        return;
      }
      if (file.size > MAX_IMG_BYTES) {
        toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 20 MB.`);
        return;
      }
    }
    const tempId = existingTempId || `tmp_${Math.random().toString(36).slice(2, 10)}`;
    const previewUrl = existingPreviewUrl || URL.createObjectURL(file);
    const controller = new AbortController();
    uploadControllersRef.current[tempId] = { controller, userAborted: false };

    if (existingTempId) {
      setPendingAttachments(prev => prev.map(a => a.tempId === tempId ? { ...a, uploading: true, error: undefined } : a));
    } else {
      setPendingAttachments(prev => [...prev, { tempId, filename: file.name, previewUrl, file, uploading: true }]);
    }

    // Run one leg with its own timeout that aborts the shared controller.
    const runLeg = async (factory, ms) => {
      const timer = setTimeout(() => controller.abort(), ms);
      try { return await factory(); }
      finally { clearTimeout(timer); }
    };

    try {
      const presign = await runLeg(() => api.post('/storage/presign-upload', {
        scope: 'chat_attachment',
        project_id: projectId,
        filename: file.name,
        content_type: file.type,
        system,
        size_bytes: file.size,
      }, { signal: controller.signal }), 20000);
      const { upload_url, file_id } = presign.data;
      await runLeg(() => fetch(upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type }, signal: controller.signal }), 120000);
      await runLeg(() => api.post(`/storage/confirm-upload/${file_id}`, {}, { signal: controller.signal }), 20000);
      delete uploadControllersRef.current[tempId];
      setPendingAttachments(prev => prev.map(a => a.tempId === tempId ? { ...a, file_id, uploading: false, error: undefined } : a));
    } catch (e) {
      const wasUserAborted = uploadControllersRef.current[tempId]?.userAborted;
      delete uploadControllersRef.current[tempId];
      if (wasUserAborted) return; // user clicked X → attachment already removed, cancelling is not a failure
      const aborted = e?.name === 'AbortError' || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
      const msg = e?.response?.data?.detail || (aborted ? 'Upload timed out — tap to retry' : (e?.message || 'Upload failed'));
      setPendingAttachments(prev => prev.map(a => a.tempId === tempId ? { ...a, uploading: false, error: msg } : a));
      toast.error(`Image upload failed: ${msg}`);
    }
  };

  const retryAttachment = (tempId) => {
    const a = pendingAttachments.find(x => x.tempId === tempId);
    if (a?.file) uploadAttachment(a.file, tempId, a.previewUrl);
  };

  // Single source of truth for "is any upload still in flight" (pure-derived, no stored copy).
  const uploading = pendingAttachments.some(a => a.uploading);

  // Revoke any remaining blob URLs + abort in-flight uploads on unmount.
  React.useEffect(() => {
    const controllers = uploadControllersRef.current;
    return () => {
      Object.values(controllers).forEach(entry => {
        try { entry.userAborted = true; entry.controller.abort(); } catch {}
      });
      pendingAttachments.forEach(a => {
        if (a.previewUrl) { try { URL.revokeObjectURL(a.previewUrl); } catch {} }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove a pending attachment — aborts any in-flight upload (silent, no toast) and
  // revokes its blob URL. Works for uploading / failed / ready alike.
  const removePendingAttachment = (tempId) => {
    const entry = uploadControllersRef.current[tempId];
    if (entry) { entry.userAborted = true; try { entry.controller.abort(); } catch {} }
    setPendingAttachments(prev => {
      const a = prev.find(x => x.tempId === tempId);
      if (a?.previewUrl) { try { URL.revokeObjectURL(a.previewUrl); } catch {} }
      return prev.filter(x => x.tempId !== tempId);
    });
  };

  const handleFiles = (files) => {
    for (const f of files) {
      if (f && f.type && f.type.startsWith('image/')) uploadAttachment(f);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f && f.type.startsWith('image/')) imageFiles.push(f);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      handleFiles(imageFiles);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) handleFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if leaving the wrapper (relatedTarget outside)
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };

  const openFilePicker = () => fileInputRef.current?.click();
  const onFileInputChange = (e) => {
    handleFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  // ── Assistant suggestion (admin only) ───────────────────────────────────
  // Delivered HERE, in the composer, rather than on a separate page: people
  // answer chats in the project drawer and All Chats, and a fourth place to go
  // looking for chat work is a worse product than no suggestion at all.
  // Non-admins get {draft: null} from the API, so this renders nothing for them.
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionUsed, setSuggestionUsed] = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [suggestionPanel, setSuggestionPanel] = useState(null);  // 'extras' | 'why' | 'flags' | null
  const [copiedStep, setCopiedStep] = useState(null);
  const [openStep, setOpenStep] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [dismissNote, setDismissNote] = useState('');
  // Whether the reviewer ever actually opened the card. On a "superseded"
  // outcome this is the difference between a real judgement (read it, wrote
  // their own) and no signal at all (never looked).
  const suggestionViewedRef = useRef(false);
  // Steps still owed AFTER the reply went out. They used to vanish with the
  // card the instant you pressed send, so "message the editor" and "escalate
  // this" were gone unless you had written them down first.
  // Each step carries its OWN draft_id: outstanding work is now carried forward
  // across replies, so the list is no longer sourced from a single draft.
  const [pendingSteps, setPendingSteps] = useState([]);
  const [stepsOpen, setStepsOpen] = useState(false);

  const [refreshing, setRefreshing] = useState(false);
  const refreshRunRef = useRef(0);

  useEffect(() => {
    setRefreshing(false);   // a watch from the previous thread or opening has been cancelled (see the cleanup)
    // MUST MATCH routes/assistant.py::REVIEWER_ROLES. The backend returns
    // {draft: null} for anyone else, so a mismatch here only ever costs a
    // pointless request -- but a NARROWER gate here silently hides the feature
    // from someone the backend would serve, which is what happened to Zach.
    if (!expanded || !projectId || readOnly
        || !['admin', 'staff'].includes(user?.role)) return;
    let gone = false;
    api.get('/assistant/draft-for-thread', { params: { project_id: projectId, system } })
      .then(r => { if (!gone) takeDraft(r.data, false); })
      .catch(() => {});
    return () => { gone = true; refreshRunRef.current += 1; };
  }, [expanded, projectId, system, readOnly, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // The card is fetched when the chat opens. A message arriving while it is open made the card look current when it
  // was not: re-ask, and if it is the same draft only its "behind" flag changes, so nothing the reviewer is reading
  // or editing is thrown away. A different draft is left for the refresh button or the next opening.
  const newestMessageId = messages.length ? messages[messages.length - 1].id : null;
  const suggestionId = suggestion?.id || null;
  useEffect(() => {
    if (!expanded || !suggestionId || !newestMessageId || readOnly) return undefined;
    let gone = false;
    api.get('/assistant/draft-for-thread', { params: { project_id: projectId, system } })
      .then(r => {
        const d = r.data?.draft;
        if (gone || !d || d.id !== suggestionId) return;
        setSuggestion(prev => (prev && prev.id === d.id && !!prev.is_stale !== !!d.is_stale ? { ...prev, is_stale: d.is_stale } : prev));
      })
      .catch(() => {});
    return () => { gone = true; };
  }, [newestMessageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Put a draft-for-thread answer on screen, starting the card fresh. `keepOpen` for the refresh button: the
  // reviewer was reading the card, so a newer draft replaces it in place instead of folding it shut.
  const takeDraft = (data, keepOpen) => {
    setSuggestion(data?.draft || null); setSuggestionUsed(false);
    setPendingSteps(data?.pending_steps?.steps || []); setSuggestionOpen(!!keepOpen && !!data?.draft); setSuggestionPanel(null);
    setCopiedStep(null); setOpenStep(null);
    setDismissing(false); setDismissNote(''); setEditing(false); setEditText('');
    suggestionViewedRef.current = !!keepOpen && !!data?.draft;
  };

  // Ryan, 2026-09-30: "sometimes that ai suggested reply is behind where we are in the chat". The refresh button
  // asks the server, which answers for free when the card is current and otherwise has the worker write a new one
  // straight away (skipping the 90-second wait). This then watches for it: a new draft replaces the card; the
  // worker finishing without one means it judged no reply is owed. refreshRunRef stops a watch when the chat closes
  // or switches project, so a late answer never lands on the wrong thread.
  const refreshSuggestion = async () => {
    if (refreshing) return;
    const run = ++refreshRunRef.current;
    const live = () => refreshRunRef.current === run;
    const before = suggestion?.id || null;
    const params = { project_id: projectId, system };
    setRefreshing(true);
    try {
      const r = await api.post('/assistant/draft-for-thread/refresh', params);
      if (!live()) return;
      const state = r.data?.refresh;
      if (state === 'current') {
        if ((r.data?.draft?.id || null) !== before) takeDraft(r.data, true);
        toast.success('This suggestion is already up to date');
        return;
      }
      if (state === 'off') { toast.error('The assistant is switched off, so there is nothing to refresh'); return; }
      if (r.data?.worker_healthy === false) {
        toast.error(`The assistant is not running right now${r.data?.worker_reason ? ` (${r.data.worker_reason})` : ''}. It will catch up once it is back.`);
        return;
      }
      toast('Writing a new suggestion. It takes about half a minute.');
      const until = Date.now() + 150000;
      while (Date.now() < until) {
        await new Promise(res => setTimeout(res, 4000));
        if (!live()) return;
        const g = await api.get('/assistant/draft-for-thread', { params });
        if (!live()) return;
        const fresh = g.data?.draft;
        if (fresh && fresh.id !== before && !fresh.is_stale) {
          takeDraft(g.data, true);
          toast.success('Suggestion updated');
          return;
        }
        if (!g.data?.queued) {
          takeDraft(g.data, !!fresh);
          toast('Nothing new to suggest: it judged this chat does not need a reply from us right now');
          return;
        }
      }
      toast('Still writing. It will show here next time you open this chat.');
    } catch (e) {
      if (live()) toast.error(e?.response?.data?.detail || 'Could not refresh the suggestion');
    } finally {
      if (live()) setRefreshing(false);
    }
  };

  // The suggested reply lives in a different field depending on the thread:
  // `client_reply` on a client chat, `internal_message` on an editor chat.
  // Reading only client_reply meant an internal-thread draft rendered NOTHING,
  // and so did the 7-in-43 client drafts whose proposal was "ask the editor
  // first, do not reply yet" -- a draft existed, the card silently did not.
  const suggestionBody = (d) => (d
    ? ((system === 'internal' ? d.internal_message?.text : d.client_reply?.text) || '')
    : '');

  // NOT named useSomething. The `use` prefix makes ESLint's rules-of-hooks treat
  // it as a React Hook, so calling it inside a callback -- which is the only way
  // to pass the edited text -- is a BUILD ERROR under `craco build`. The dev
  // server only warns, so this failed in Railway and nowhere else.
  const applySuggestion = (override) => {
    const body = (typeof override === 'string' ? override : suggestionBody(suggestion));
    if (!body) return;
    setNewMsg(body);
    setSuggestionUsed(true);
    setTimeout(() => { try { textareaRef.current?.focus(); autoResize(); } catch {} }, 0);
  };

  // A dismissal without a reason taught us nothing: every one of them landed as
  // "not_needed" whether the draft was wrong on facts or whether you simply
  // phoned the client instead. Those are different facts and only one of them
  // is the model's problem (see store.signal_for).
  //
  // TONE IS DELIBERATELY ABSENT, ON AN ASSUMPTION THAT IS NOT YET TESTED: that a
  // tone problem gets EDITED rather than dismissed, so the edit diff already
  // carries it. That only holds while the draft is close enough to be worth
  // editing. A badly-toned draft nobody would send gets DISMISSED instead, and
  // with no tone option it lands under "Wrong call" -- filing a voice problem as
  // a judgement error and pointing the weekly pass at the wrong fix.
  // TO CHECK once real dismissals accumulate: read the notes on `wrong_call`
  // rows. If they keep describing register rather than a decision, tone needs
  // its own reason. See ROADMAP "Open questions".
  const DISMISS_REASONS = [
    { key: 'handled_elsewhere', label: 'Handled another way' },
    { key: 'wrong_facts', label: 'Facts were wrong' },
    { key: 'wrong_call', label: 'Wrong call' },
  ];

  const dismissSuggestion = async (reason) => {
    const id = suggestion?.id;
    const note = dismissNote.trim();
    setSuggestion(null); setSuggestionUsed(false);
    setDismissing(false); setDismissNote('');
    if (!id) return;
    try {
      await api.post(`/assistant/drafts/${id}/record-outcome`,
        { outcome: 'rejected', reject_reason: reason, reviewer_note: note || undefined });
    } catch { /* dismissing is a local convenience; a failed record is not worth a toast */ }
  };

  // Rendered directly above the composer in BOTH layouts (compact drawer + full
  // page). THE REPLY IS THE PRODUCT: it gets the space. Everything else --
  // the editor message, the next steps, the reasoning, the rule flags -- sits
  // behind a chip you tap, because a suggestion that fills the drawer is worse
  // than no suggestion (operator, 2026-08-21).
  // Component scope: BOTH the suggestion card and the pending-steps strip copy
  // step text, and duplicating it would be two places for the promise-handling
  // below to drift apart.
  //
  // writeText REJECTS rather than throws when the clipboard is unavailable
  // (insecure origin, denied permission), so a synchronous try/catch would let
  // the button flash "Copied" over an empty clipboard. Confirm off the promise.
  const copyStep = (key, txt) => {
    const ok = () => {
      setCopiedStep(key);
      setTimeout(() => setCopiedStep(k => (k === key ? null : k)), 1500);
    };
    const fail = () => toast.error('Could not copy. Select the text instead.');
    try {
      const p = navigator.clipboard?.writeText(txt);
      if (p && typeof p.then === 'function') p.then(ok, fail); else ok();
    } catch { fail(); }
  };

  // DONE and DISMISSED are different facts, not two words for the same one:
  // "we did it" is a suggestion that worked, "we did not need to" is not.
  const closeStep = async (step, state) => {
    setPendingSteps(prev => prev.filter(
      s => !(s.index === step.index && s.draft_id === step.draft_id)));
    if (!step.draft_id) return;
    try {
      await api.post(`/assistant/drafts/${step.draft_id}/steps/${step.index}/done`, { state });
    } catch { /* closing is a local convenience; a failed record is not worth a toast */ }
  };

  const renderPendingSteps = () => {
    if (readOnly || !pendingSteps.length) return null;
    const anyRequired = pendingSteps.some(s => s.required);
    return (
      <div className={`mb-2 rounded-lg border overflow-hidden ${
          anyRequired ? 'border-amber-500/50 bg-amber-500/[0.05]'
                      : 'border-ug-border bg-ug-surface/40'}`}
        data-testid={`chat-pending-steps-${system}`}>
        <button type="button" onClick={() => setStepsOpen(o => !o)}
          data-testid={`chat-pending-steps-toggle-${system}`}
          className="tap-auto w-full flex items-center gap-1.5 px-2.5 py-1.5 text-left hover:bg-white/[0.03] transition-colors">
          {anyRequired
            ? <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
            : <Check className="w-3 h-3 text-slate-500 shrink-0" />}
          <span className={`font-sans text-[9px] font-semibold uppercase tracking-wider shrink-0 ${
              anyRequired ? 'text-amber-400' : 'text-slate-500'}`}>
            {pendingSteps.length} step{pendingSteps.length === 1 ? '' : 's'} still to do
          </span>
          {!stepsOpen && (
            <span className="text-[11px] text-zinc-100/50 truncate flex-1 min-w-0">
              {pendingSteps[0]?.text}
            </span>
          )}
          <span className="ml-auto shrink-0">
            {stepsOpen ? <ChevronUp className="w-3 h-3 text-slate-500" />
                       : <ChevronDown className="w-3 h-3 text-slate-500" />}
          </span>
        </button>
        {stepsOpen && (
          <div className="px-2.5 pb-2.5 space-y-2">
            {pendingSteps.map(st => (
              <div key={`${st.draft_id}-${st.index}`}
                data-testid={`chat-pending-step-${st.index}-${system}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`font-sans text-[8px] font-bold uppercase tracking-wider shrink-0 ${
                      st.required ? 'text-amber-300' : 'text-slate-500'}`}>
                    {st.label}
                  </span>
                  <button type="button" onClick={() => copyStep(`pend-${st.index}`, st.text)}
                    data-testid={`chat-pending-copy-${st.index}-${system}`}
                    className="tap-auto ml-auto shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded border border-ug-border text-slate-400 font-sans text-[8px] font-semibold uppercase tracking-wider hover:text-zinc-200 transition-colors">
                    {copiedStep === `pend-${st.index}` ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}
                    {copiedStep === `pend-${st.index}` ? 'Copied' : 'Copy'}
                  </button>
                  <button type="button" onClick={() => closeStep(st, 'done')}
                    data-testid={`chat-pending-done-${st.index}-${system}`}
                    className="tap-auto shrink-0 px-1.5 py-0.5 rounded border border-oxidized-teal/40 text-oxidized-teal font-sans text-[8px] font-semibold uppercase tracking-wider hover:bg-oxidized-teal/10 transition-colors">
                    Done
                  </button>
                  <button type="button" onClick={() => closeStep(st, 'dismissed')}
                    title="Not needed, or already handled another way"
                    data-testid={`chat-pending-dismiss-${st.index}-${system}`}
                    className="tap-auto shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-600 hover:text-slate-300 hover:bg-white/[0.06] transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-[10px] text-zinc-100/70 leading-relaxed whitespace-pre-wrap mt-0.5">
                  {st.text}
                </p>
              </div>
            ))}
            <p className="text-[9px] text-slate-600">
              Required steps carry across replies until you mark them done or X them
              out. Anything untouched for a week goes quiet on its own.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderSuggestion = () => {
    if (!suggestion || readOnly) return null;
    const text = suggestionBody(suggestion);
    const flags = suggestion.rule_flags || [];
    const why = suggestion.confidence_note || '';
    // A STALE draft answers the message BEFORE the ones on screen. It stays
    // visible (often still useful context) but is NOT offered as one-click
    // sendable: the reviewer's eye goes to the gold button, not to a badge.
    // A fresher draft now replaces it on the next sweep.
    const stale = !!suggestion.is_stale;
    const hasReply = !!text && !stale;
    const cites = suggestion.citations || [];
    // REQUIRED vs merely suggested is decided server-side, in
    // app/assistant/followups.py, so the rule is one testable function instead
    // of a guess spread through JSX -- and so it applies to drafts written
    // before the distinction existed. A missing/failed split degrades to
    // "everything is a suggestion": quiet is the safe default for a loud
    // element, since an alarm that fires on everything stops meaning anything.
    const fu = suggestion.followups || {};
    const required = fu.required || [];
    const suggested = fu.suggested || [];
    const extras = suggested.length;
    // Nothing to say and nothing to do: the only case where showing nothing is
    // right. Previously ANY draft without a client_reply fell into this.
    // A draft that is BEHIND still has something to say (it is what the refresh button is for): until 2026-09-30 a
    // stale draft with no steps fell in here and vanished, taking the "behind" label and any way to refresh with it.
    if (!hasReply && !(stale && text) && required.length === 0 && extras === 0) return null;

    const chip = (key, label, tone = 'muted') => (
      <button type="button" onClick={() => setSuggestionPanel(p => (p === key ? null : key))}
        data-testid={`chat-suggestion-chip-${key}-${system}`}
        className={`tap-auto px-2 py-0.5 rounded-full border font-sans text-[9px] font-medium transition-colors ${
          suggestionPanel === key ? 'bg-gold-leaf/20 text-ug-gold border-gold-leaf/40'
          : tone === 'warn' ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10'
          : 'border-ug-border text-slate-400 hover:text-zinc-200'}`}>
        {label}
      </button>
    );

    return (
      <div className={`mb-2 rounded-lg border overflow-hidden ${
          required.length > 0 ? 'border-amber-500/50 bg-amber-500/[0.05]'
                              : 'border-gold-leaf/25 bg-gold-leaf/[0.04]'}`}
        data-testid={`chat-suggestion-${system}`}
        data-required={required.length > 0 ? 'true' : 'false'}>
        <div className="flex items-stretch">
        <button type="button"
          onClick={() => setSuggestionOpen(o => { if (!o) suggestionViewedRef.current = true; return !o; })}
          data-testid={`chat-suggestion-toggle-${system}`}
          className="tap-auto flex-1 min-w-0 flex items-center gap-1.5 px-2.5 py-1.5 text-left hover:bg-gold-leaf/[0.06] transition-colors">
          <Sparkles className="w-3 h-3 text-ug-gold shrink-0" />
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold shrink-0">
            {stale ? 'Written before their latest message'
                   : (hasReply ? 'Suggested reply' : 'Suggested next step')}
          </span>
          {!suggestionOpen && (
            <span className="text-[11px] text-zinc-100/60 truncate flex-1 min-w-0">
              {hasReply ? text : (required[0]?.text || suggested[0]?.text || '')}
            </span>
          )}
          {required.length > 0 && !suggestionOpen && (
            <span className="tap-auto shrink-0 flex items-center gap-1 text-[8px] font-sans font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500 text-obsidian"
              data-testid={`chat-suggestion-required-badge-${system}`}>
              <AlertTriangle className="w-2.5 h-2.5" />
              Action required
            </span>
          )}
          {suggestion.is_stale && (
            <span className="tap-auto shrink-0 text-[8px] font-sans font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-ug-status-red/15 text-ug-status-red">
              they replied since
            </span>
          )}
          <span className="ml-auto shrink-0">
            {suggestionOpen ? <ChevronUp className="w-3 h-3 text-slate-500" />
                            : <ChevronDown className="w-3 h-3 text-slate-500" />}
          </span>
        </button>
        <button type="button" onClick={refreshSuggestion} disabled={refreshing}
          data-testid={`chat-suggestion-refresh-${system}`}
          data-refreshing={refreshing ? 'true' : 'false'}
          title={refreshing ? 'Writing a new suggestion' : 'Check for a newer suggestion'}
          aria-label="Refresh the suggested reply"
          data-behind={stale ? 'true' : 'false'}
          className={`tap-auto shrink-0 flex items-center px-2 transition-colors hover:bg-gold-leaf/[0.06] disabled:cursor-wait ${
            stale ? 'text-ug-gold hover:text-ug-gold' : 'text-slate-500 hover:text-zinc-200'}`}>
          {/* Icon only: a word here pushed the open/close arrow off the header. Behind = the pressable ink. */}
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
        </div>

        {suggestionOpen && (
          <div className="px-2.5 pb-2.5">
            {/* The reply. Capped and scrollable so a long draft can never push
                the message box off screen. */}
            {hasReply && editing ? (
              /* Edited HERE rather than only in the composer so the reasoning and
                 the required steps stay on screen while you rewrite. The RECORDING
                 is unchanged: the outcome is still written on send, comparing what
                 was sent against the original draft, so the diff -- which is the
                 training signal -- is captured exactly as before. */
              <div data-testid={`chat-suggestion-editor-${system}`}>
                <textarea value={editText} onChange={e => setEditText(e.target.value)}
                  rows={6} autoFocus
                  data-testid={`chat-suggestion-edit-input-${system}`}
                  className="w-full bg-ug-surface border border-gold-leaf/40 rounded-md text-[12px] text-zinc-100/90 p-2 leading-relaxed resize-y outline-none focus:border-gold-leaf/70" />
                <div className="flex items-center gap-1.5 mt-1.5">
                  <button type="button"
                    onClick={() => { const t2 = editText.trim(); if (!t2) return;
                                     applySuggestion(t2); setEditing(false); }}
                    data-testid={`chat-suggestion-edit-save-${system}`}
                    className="tap-auto px-2.5 py-1 rounded bg-gold-leaf text-obsidian font-sans text-[9px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/85 transition-colors">
                    Use edited version
                  </button>
                  <button type="button" onClick={() => { setEditing(false); setEditText(''); }}
                    data-testid={`chat-suggestion-edit-cancel-${system}`}
                    className="tap-auto px-2.5 py-1 rounded border border-ug-border text-slate-400 font-sans text-[9px] font-semibold uppercase tracking-wider hover:text-zinc-200 transition-colors">
                    Cancel
                  </button>
                  <span className="text-[9px] text-slate-600 ml-1">
                    Your changes are what it learns from.
                  </span>
                </div>
              </div>
            ) : hasReply ? (
              <p className="text-[12px] text-zinc-100/90 leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto"
                data-testid={`chat-suggestion-text-${system}`}>{text}</p>
            ) : stale ? (
              <div data-testid={`chat-suggestion-stale-${system}`}>
                <p className="text-[11px] text-slate-400 leading-relaxed mb-1.5">
                  They have written since this was drafted, so it does not answer
                  what is on screen. Press the refresh arrow for a fresh one now, or one is written on the next pass.
                </p>
                <p className="text-[11px] text-zinc-100/50 leading-relaxed whitespace-pre-wrap max-h-24 overflow-y-auto italic">
                  {text}
                </p>
              </div>
            ) : (
              <p className="text-[11px] text-slate-400 leading-relaxed"
                data-testid={`chat-suggestion-noreply-${system}`}>
                No reply suggested yet -- it proposes doing this first.
              </p>
            )}

            {/* Required steps are never hidden behind a tap. The reply above is
                a promise ("let me check with your editor and come back today")
                and sending it alone leaves the client waiting on something
                nobody did -- which reads as an answer, so nobody chases it. */}
            {required.length > 0 && (
              <div className="mt-2 rounded border border-amber-500/60 overflow-hidden"
                data-testid={`chat-suggestion-required-${system}`}>
                <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-500">
                  <AlertTriangle className="w-3 h-3 text-obsidian shrink-0" />
                  <span className="shrink-0 font-sans text-[9px] font-bold uppercase tracking-wider text-obsidian">
                    Action required{required.length > 1 ? ` · ${required.length} steps` : ''}
                  </span>
                  {/* Container-width, not viewport-width: in the drawer this bar
                      is ~440px, in All Chats it is far wider. The secondary label
                      truncates away; the alarm itself never shrinks. */}
                  <span className="ml-auto min-w-0 truncate font-sans text-[8px] font-semibold uppercase tracking-wider text-obsidian/80">
                    the reply promises this
                  </span>
                </div>
                {/* Loud, not tall. The operator rejected a card that filled the
                    drawer, and an alarm that buries the message box gets closed
                    unread. Each step previews in two lines and opens on tap. */}
                <div className="bg-amber-500/[0.07] px-2 py-1.5 space-y-1.5">
                  {required.map((r, i) => (
                    <div key={i} data-testid={`chat-suggestion-required-step-${i}-${system}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-sans text-[8px] font-bold uppercase tracking-wider text-amber-300 shrink-0">
                          {r.label}
                        </span>
                        <button type="button" onClick={() => copyStep(`req-${i}`, r.text)}
                          data-testid={`chat-suggestion-required-copy-${i}-${system}`}
                          className="tap-auto ml-auto shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 font-sans text-[8px] font-semibold uppercase tracking-wider hover:bg-amber-500/15 transition-colors">
                          {copiedStep === `req-${i}` ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}
                          {copiedStep === `req-${i}` ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      <button type="button"
                        onClick={() => setOpenStep(k => (k === `req-${i}` ? null : `req-${i}`))}
                        data-testid={`chat-suggestion-required-expand-${i}-${system}`}
                        className="tap-auto block w-full text-left mt-0.5">
                        {/* zinc-100/85, not amber-100: index.css maps the stock
                            amber -2/-3/-4 steps for light mode but never -100,
                            so amber-100 body copy was invisible on paper. The
                            warm identity is carried by the bar and the tint. */}
                        <span className={`text-[10px] text-zinc-100/85 leading-relaxed whitespace-pre-wrap ${
                          openStep === `req-${i}` ? 'block max-h-24 overflow-y-auto' : 'line-clamp-1'}`}>
                          {r.text}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {hasReply && !editing && (
                <button type="button"
                  onClick={() => { setEditText(text); setEditing(true); }}
                  data-testid={`chat-suggestion-edit-${system}`}
                  className="tap-auto px-2.5 py-1 rounded border border-gold-leaf/40 text-ug-gold font-sans text-[9px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/10 transition-colors">
                  Edit
                </button>
              )}
              {hasReply && !editing && (
                <button type="button" onClick={() => applySuggestion()} data-testid={`chat-suggestion-use-${system}`}
                  className="tap-auto px-2.5 py-1 rounded bg-gold-leaf text-obsidian font-sans text-[9px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/85 transition-colors">
                  Use this
                </button>
              )}
              <button type="button" onClick={() => setDismissing(true)}
                data-testid={`chat-suggestion-dismiss-${system}`}
                className="tap-auto px-2.5 py-1 rounded border border-ug-border text-slate-400 font-sans text-[9px] font-semibold uppercase tracking-wider hover:text-zinc-200 transition-colors">
                Dismiss
              </button>
              {extras > 0 && chip('extras', required.length > 0
                ? `Also suggested (${extras})`
                : `${extras} more step${extras === 1 ? '' : 's'}`)}
              {(why || cites.length > 0) && chip('why', 'Why')}
              {flags.length > 0 && chip('flags', `${flags.length} to check`, 'warn')}
            </div>
            {dismissing && (
              <div className="mt-2 rounded border border-ug-border-subtle bg-ug-surface/40 px-2 py-1.5"
                data-testid={`chat-suggestion-dismiss-menu-${system}`}>
                <span className="font-sans text-[8px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                  Why? one tap
                </span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {DISMISS_REASONS.map(r => (
                    <button key={r.key} type="button" onClick={() => dismissSuggestion(r.key)}
                      data-testid={`chat-suggestion-reason-${r.key}-${system}`}
                      className="tap-auto px-2 py-0.5 rounded-full border border-ug-border text-slate-400 font-sans text-[9px] font-medium hover:text-zinc-200 hover:border-slate-500 transition-colors">
                      {r.label}
                    </button>
                  ))}
                  <button type="button" onClick={() => { setDismissing(false); setDismissNote(''); }}
                    data-testid={`chat-suggestion-dismiss-cancel-${system}`}
                    className="tap-auto ml-auto text-[9px] text-slate-600 hover:text-slate-400 transition-colors">
                    Cancel
                  </button>
                </div>
                {/* Optional, and it stays optional. The richest corrections are
                    prose ("we cannot do that in the portal"), but a field you
                    are forced to fill is a button you stop pressing. */}
                <input type="text" value={dismissNote} onChange={e => setDismissNote(e.target.value)}
                  maxLength={500} placeholder="What should it have known? (optional)"
                  data-testid={`chat-suggestion-dismiss-note-${system}`}
                  className="tap-auto mt-1.5 w-full bg-transparent border-b border-ug-border-subtle text-[10px] text-zinc-100/80 placeholder:text-slate-600 focus:outline-none focus:border-slate-500 pb-0.5" />
              </div>
            )}

            {hasReply && <p className="text-[9px] text-slate-600 mt-1">
              {required.length > 0
                ? `Fills the box below. You still press send -- and the step${required.length === 1 ? '' : 's'} above ${required.length === 1 ? 'is' : 'are'} not optional.`
                : 'Fills the box below. You still press send -- and if you change it, that edit is what it learns from.'}
            </p>}

            {suggestionPanel === 'extras' && (
              <div className="mt-2 space-y-1.5" data-testid={`chat-suggestion-extras-${system}`}>
                {suggested.map((sg, i) => (
                  <div key={i} className="rounded border border-ug-border-subtle bg-ug-surface/40 px-2 py-1.5">
                    <span className="font-sans text-[8px] font-semibold uppercase tracking-wider text-slate-500 block mb-0.5">
                      {sg.label}
                    </span>
                    <p className="text-[10px] text-zinc-100/70 leading-relaxed whitespace-pre-wrap">{sg.text}</p>
                  </div>
                ))}
              </div>
            )}
            {suggestionPanel === 'why' && (
              <div className="mt-2 rounded border border-ug-border-subtle bg-ug-surface/40 px-2 py-1.5"
                data-testid={`chat-suggestion-why-${system}`}>
                {why && <p className="text-[10px] text-slate-400 leading-relaxed">{why}</p>}
                {cites.length > 0 && (
                  <p className="text-[9px] text-slate-600 mt-1">{cites.join(' · ')}</p>
                )}
              </div>
            )}
            {suggestionPanel === 'flags' && (
              <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/[0.06] px-2 py-1.5"
                data-testid={`chat-suggestion-flags-${system}`}>
                {flags.map((f, i) => (
                  <p key={i} className="text-[10px] text-amber-300/90 leading-relaxed">{f}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleSend = async () => {
    const trimmed = newMsg.trim();
    const readyAttachments = pendingAttachments.filter(a => a.file_id && !a.uploading);
    const stillUploading = pendingAttachments.some(a => a.uploading);
    if (stillUploading) { toast.error('Wait for images to finish uploading'); return; }
    if (!trimmed && readyAttachments.length === 0) return;
    if (sending) return;
    if (trimmed.length > MAX_MSG_LEN) {
      toast.error(`Message too long (${trimmed.length}/${MAX_MSG_LEN})`);
      return;
    }
    setSending(true);
    try {
      // Reconcile mentions: drop any stale id whose @{name} is no longer present in the
      // sent text (e.g. a mention that was selected then deleted/retyped before sending).
      const effectiveMentions = pendingMentions.filter(id => {
        const u = taggableUsers.find(t => t.id === id);
        return u && mentionInText(u.name, trimmed);
      });
      const res = await api.post(basePath, {
        body: trimmed,
        mentions: effectiveMentions,
        urgent,
        attachments: readyAttachments.map(a => ({ file_id: a.file_id, filename: a.filename })),
      });
      userJustSentRef.current = true;
      setMessages(prev => [...prev, res.data]);
      // If this send started from a suggestion, close the loop: whether the
      // text was kept or rewritten IS the training signal. Best-effort -- the
      // message is already out, so a failure here must not surface as an error.
      // SUPERSEDED is the case this used to throw away. If a draft was open on
      // this thread and you wrote your own reply instead, that pair -- what the
      // model proposed vs what you actually sent, same context, same moment --
      // is the best training data the system produces, and it costs zero
      // clicks. `viewed` separates a real judgement (you read it and went your
      // own way) from no signal at all (you never opened it).
      if (suggestion?.id) {
        const original = suggestionBody(suggestion).trim();
        const outcome = !suggestionUsed ? 'superseded'
          : (trimmed === original ? 'approved' : 'edited');
        api.post(`/assistant/drafts/${suggestion.id}/record-outcome`, {
          outcome,
          final_text: trimmed,
          sent_message_id: res.data?.id || '',
          ...(outcome === 'superseded' ? { viewed: suggestionViewedRef.current } : {}),
        }).catch(() => {});
        // Carry the steps forward. The reply is gone; the work it promised is
        // not, and the server will serve the same list on the next page load.
        const fu = suggestion.followups || {};
        const carried = [...(fu.required || []), ...(fu.suggested || [])]
          .map((st, i) => ({ ...st, index: i, required: i < (fu.required || []).length }));
        setPendingSteps(carried.map(st => ({ ...st, draft_id: suggestion.id })));
        setStepsOpen(false);
        setSuggestion(null); setSuggestionUsed(false);
      }
      setNewMsg(''); setPendingMentions([]); setUrgent(false);
      // Revoke all blob URLs and clear pending attachments
      pendingAttachments.forEach(a => { if (a.previewUrl) { try { URL.revokeObjectURL(a.previewUrl); } catch {} } });
      setPendingAttachments([]);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send';
      toast.error(msg);
    } finally { setSending(false); }
    return false;
  };

  // Optimistic toggle: a reaction should feel instant. The server response is
  // authoritative and replaces the guess; on failure we put the old array back
  // rather than leaving a reaction on screen that never persisted.
  const toggleReaction = async (msg, emoji) => {
    if (readOnly) return;
    const before = msg.reactions || [];
    const had = before.some(r => r.user_id === user.id && r.emoji === emoji);
    const optimistic = had
      ? before.filter(r => !(r.user_id === user.id && r.emoji === emoji))
      : [...before, { emoji, user_id: user.id, user_name: user.name,
                      user_role: user.role, at: new Date().toISOString() }];
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: optimistic } : m));
    try {
      const res = await api.post(`/chat/${system}/messages/${msg.id}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: res.data.reactions } : m));
    } catch {
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: before } : m));
      toast.error('Could not save that reaction');
    }
  };

  const handleDeleteMessage = async (msg) => {
    if (deletingMsgId) return;
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    setDeletingMsgId(msg.id);
    try {
      await api.delete(`/chat/${system}/messages/${msg.id}`);
      setMessages(prev => prev.filter(m => m.id !== msg.id));
      toast.success('Message deleted');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to delete';
      toast.error(detail);
    } finally { setDeletingMsgId(null); }
  };

  // Suggestions follow the CARET, not the last "@" in the message (lib/mentions.js). The
  // old rule read everything after the last "@" as the name being typed, so an "@" typed
  // into a written message offered nobody, and picking a name deleted the rest of the
  // message. A text selection (not a bare caret) never offers names.
  const syncMentions = (el) => {
    const m = el.selectionStart === el.selectionEnd ? mentionQueryAt(el.value, el.selectionStart) : null;
    if (m) { setShowMentions(true); setMentionSearch(m.query.toLowerCase()); }
    else setShowMentions(false);
  };

  const handleInputChange = (e) => {
    setNewMsg(e.target.value);
    syncMentions(e.target);
  };

  // Moving the caret with the arrow keys or a click opens or closes the suggestions too.
  const handleCaretMove = (e) => syncMentions(e.target);

  const insertMention = (u) => {
    const el = textareaRef.current;
    const caret = el ? el.selectionStart : newMsg.length;
    const m = mentionQueryAt(newMsg, caret);
    setShowMentions(false);
    if (!m) return;
    const next = insertMentionAt(newMsg, m.start, caret, u.name);
    setNewMsg(next.text);
    setPendingMentions(prev => [...prev.filter(id => id !== u.id), u.id]);
    // After React has rendered the new value, put the caret right after the name.
    setTimeout(() => { try { el?.focus(); el?.setSelectionRange(next.caret, next.caret); } catch {} }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (showMentions) {
        const filtered = taggableUsers.filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch));
        if (filtered.length > 0) {
          e.preventDefault();
          insertMention(filtered[0]);
          return;
        }
      }
      if (e.shiftKey) {
        return; // allow default: inserts newline in textarea
      }
      e.preventDefault();
      handleSend();
    }
  };

  // THE BIG BOX: a window of its own (lib/chatBox.js popOutPlace) holding the same words, mentions and images as the
  // small box. Made for long messages, so Enter is a new line there and Ctrl or Cmd + Enter sends; a sent message closes
  // it, and so do Escape and its close button, the words kept. The small box stays in the drawer, saying where you are.
  const sendFromBigBox = async () => { if (await handleSend()) setBigBox(false); };
  const handleBigBoxKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (showMentions) {
      const filtered = taggableUsers.filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch));
      if (filtered.length > 0) { e.preventDefault(); insertMention(filtered[0]); return; }
    }
    if (e.metaKey || e.ctrlKey) { e.preventDefault(); sendFromBigBox(); }
  };
  const renderBigBox = () => {
    if (!bigBox || readOnly) return null;
    const mentionable = showMentions ? taggableUsers.filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch)) : [];
    const images = pendingAttachments.length;
    return ReactDOM.createPortal(
      <div role="dialog" aria-label="Bigger message box" data-testid={`chat-box-window-${system}`}
        className={`ug-modal ug-modal-card fixed z-50 flex flex-col bg-ug-raised border border-ug-border-strong rounded-lg shadow-2xl ${dragOver ? 'ring-2 ring-ug-gold/40' : ''}`}
        style={popOutPlace(viewW)}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          e.preventDefault(); e.stopPropagation();
          if (showMentions) setShowMentions(false); else setBigBox(false);
        }}
        onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-ug-border shrink-0">
          <span className={`font-sans text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5 min-w-0 ${system === 'client' ? 'text-ug-gold' : 'text-oxidized-teal'}`}>
            {systemIcon} {systemLabel}
            {clientNameSuffix && <span className="font-sans text-[10px] font-normal normal-case tracking-normal text-slate-500 truncate">· {clientNameSuffix}</span>}
          </span>
          <button type="button" data-testid={`chat-box-window-close-${system}`} onClick={() => setBigBox(false)}
            title="Back to the small box" aria-label="Back to the small box"
            className="tap-auto w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:text-ug-gold transition-colors shrink-0">
            <Minimize2 className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0 relative p-3">
          {mentionable.length > 0 && (
            <div className="absolute bottom-6 left-6 w-56 bg-ug-raised border border-ug-border-strong rounded-lg shadow-xl z-10 py-1 max-h-48 overflow-y-auto" data-testid="mention-list">
              {mentionable.map(u => (
                <button key={u.id} onClick={() => insertMention(u)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.05] text-left tap-sm">
                  <span className="text-[12px] text-zinc-100">{u.name}</span>
                  <span className="text-[9px] text-slate-600 font-mono uppercase">{u.role}</span>
                </button>
              ))}
            </div>
          )}
          <textarea ref={textareaRef} data-testid={`chat-box-window-input-${system}`} value={newMsg} onChange={handleInputChange} onSelect={handleCaretMove} onKeyDown={handleBigBoxKeyDown} onPaste={handlePaste} maxLength={MAX_MSG_LEN}
            placeholder="Type @ to mention someone. Enter starts a new line."
            className="bg-ug-surface border border-ug-border text-zinc-100 text-sm leading-relaxed placeholder:text-white/40 rounded-md p-3 w-full h-full resize-none overflow-y-auto outline-none focus:border-gold-leaf/40" />
        </div>
        <div className="flex items-center gap-3 px-4 py-2.5 border-t border-ug-border shrink-0">
          <button type="button" data-testid={`chat-box-window-attach-${system}`} onClick={openFilePicker} title="Attach image"
            className="tap-sm w-9 h-9 flex items-center justify-center text-slate-500 hover:text-ug-gold rounded transition-colors shrink-0">
            <Paperclip className="w-4 h-4" />
          </button>
          {images > 0 && <span className="text-[11px] text-slate-400">{images === 1 ? '1 image attached' : `${images} images attached`}</span>}
          <span data-testid={`chat-box-window-hint-${system}`} className={`ml-auto font-mono text-[10px] ${newMsg.length >= MAX_MSG_LEN ? 'text-ug-status-red' : 'text-slate-500'}`}>
            {newMsg.length >= SOFT_WARN_AT ? `${newMsg.length}/${MAX_MSG_LEN}` : 'Ctrl or Cmd + Enter sends'}
          </span>
          <Button data-testid={`chat-box-window-send-${system}`} onClick={sendFromBigBox} disabled={sending || uploading} size="sm"
            className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-9 px-4 gap-2 shrink-0 tap-sm">
            Send <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>,
      document.body
    );
  };

  const isMe = (msg) => msg.author_id === user.id;
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const systemLabel = system === 'internal'
    ? (user.role === 'editor' ? 'Chat with Admin (not the client)' : 'Internal Editor Chat')
    : (user.role === 'client' ? 'Chat with your Project Manager' : 'Client Chat');
  const clientNameSuffix = system === 'client' && clientName && user.role !== 'client' && user.role !== 'editor' ? clientName : null;
  const systemIcon = system === 'internal' ? <Lock className="w-3 h-3 tap-auto" /> : <Send className="w-3 h-3 tap-auto" />;

  // ─── URL linkification ───
  const linkifyText = (text, keyPrefix) => {
    if (!text || typeof text !== 'string') return text;
    return text.split(/(https?:\/\/[^\s<]+)/g).map((part, i) =>
      part.match(/^https?:\/\//) ? <a key={`${keyPrefix}-${i}`} href={part} target="_blank" rel="noopener noreferrer" className="text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all">{part}</a> : part
    );
  };

  // ─── Mention highlighting using known user names ───
  const renderMentions = (text) => {
    if (!text) return null;
    // The people who can be tagged, AND the viewer: /users/taggable leaves the viewer out (nobody tags themselves), so
    // a message tagging YOU was the one that showed no highlight -- the case a tag exists for.
    const names = [...new Set([...taggableUsers.map(u => u.name), user?.name].filter(Boolean))].sort((a, b) => b.length - a.length);
    if (names.length === 0) {
      return text.split(/(@[A-Z][a-zA-Z]+(?:\s[A-Z][a-zA-Z]+){0,2})/g).map((part, i) =>
        part.startsWith('@') ? <span key={i} data-mention="true" className="text-ug-gold font-medium">{part}</span> : linkifyText(part, `f-${i}`)
      );
    }
    const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`(@(?:${escaped.join('|')}))`, 'gi');
    const nameSet = new Set(names.map(n => n.toLowerCase()));
    return text.split(re).map((part, i) => {
      if (part.startsWith('@') && nameSet.has(part.slice(1).toLowerCase())) {
        return <span key={i} data-mention="true" className="text-ug-gold font-medium">{part}</span>;
      }
      return linkifyText(part, `m-${i}`);
    });
  };

  // ─── Message bubble (shared between compact + full) ───
  const MessageBubble = ({ msg }) => {
    const photoFailed = failedPhotos[msg.author_id];
    const showPhoto = msg.author_photo && !photoFailed;
    const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
    const canDelete = !msg.deleted && (msg.author_id === user.id || user.role === 'admin' || user.role === 'staff');
    const avatarNode = showPhoto ? (
      <img src={`${process.env.REACT_APP_BACKEND_URL}${msg.author_photo}`} alt="" className="ug-avatar w-5 h-5 rounded-full shrink-0 object-cover"
        style={{ border: `1.5px solid ${ROLE_COLORS[msg.author_role]}30` }}
        onError={() => setFailedPhotos(prev => ({ ...prev, [msg.author_id]: true }))} />
    ) : (
      <div className="ug-avatar w-5 h-5 rounded-full shrink-0 flex items-center justify-center"
        style={{ backgroundColor: `${ROLE_COLORS[msg.author_role]}${AVATAR_WASH_HEX}`, border: `1.5px solid ${ROLE_COLORS[msg.author_role]}30` }}>
        <span className="font-sans text-[7px] font-semibold" style={{ color: roleInk(msg.author_role, isLight) }}>
          {getInitials(msg.author_name)}
        </span>
      </div>
    );
    return (
    <div data-testid={`chat-message-${msg.id}`} className={`group flex pb-2 ${isMe(msg) ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[75%]">
        <div className={`flex items-center gap-1.5 mb-1.5 ${isMe(msg) ? 'justify-end' : ''}`}>
          {!isMe(msg) && avatarNode}
          <span className="font-sans text-[8px] font-medium uppercase tracking-wider font-semibold" style={{ color: roleInk(msg.author_role, isLight) }}>{msg.author_name}</span>
          <span className="font-sans text-[8px] font-medium text-slate-600">{(() => { const d = new Date(msg.created_at); const now = new Date(); const isToday = d.toDateString() === now.toDateString(); return isToday ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`; })()}</span>
          {canDelete && (
            <button
              data-testid={`chat-message-delete-${msg.id}`}
              onClick={() => handleDeleteMessage(msg)}
              disabled={deletingMsgId === msg.id}
              title="Delete message"
              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-ug-status-red tap-sm disabled:opacity-50">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          {isMe(msg) && avatarNode}
        </div>
        {attachments.length > 0 && (
          <div className={`mb-1.5 flex flex-wrap gap-1.5 ${isMe(msg) ? 'justify-end' : ''}`}>
            {attachments.map((att) => {
              const ready = imageState[att.file_id]?.status === 'ready';
              /* Not a <button>: the failure chip contains its own Try again control,
                 and a button inside a button is invalid. Click-to-zoom is attached
                 only when there is actually an image to zoom. */
              return (
                <div key={att.file_id}
                     className={`w-[200px] max-w-full ${ready ? 'cursor-zoom-in' : ''}`}
                     title={att.filename}
                     onClick={ready ? () => setLightboxFid(att.file_id) : undefined}>
                  <AttachmentImage
                    state={imageState[att.file_id]}
                    filename={att.filename}
                    onRetry={() => retryImage(att.file_id)}
                    onLoaded={() => reportLoaded(att.file_id)}
                    onLoadError={() => reportError(att.file_id)}
                    testId={`chat-attachment-thumb-${att.file_id}`} />
                </div>
              );
            })}
          </div>
        )}
        {(msg.body || msg.text) && (
          <div className={`ug-bubble px-3 py-2 rounded-lg text-[13px] leading-relaxed whitespace-pre-wrap ${
            isMe(msg) ? 'bg-ug-gold/15 border border-ug-gold/20 text-zinc-100 rounded-tr-sm' : 'bg-ug-surface border border-ug-border text-zinc-300 rounded-tl-sm'}`}>
            {renderMentions(msg.body || msg.text || '')}
            {msg.urgent && <span className="ml-1 text-ug-status-red text-[9px] font-mono">URGENT</span>}
          </div>
        )}
        {!readOnly || (msg.reactions || []).length > 0 ? (
          <ReactionRow msg={msg} mine={user} onToggle={toggleReaction} readOnly={readOnly}
            open={picker?.id === msg.id}
            anchorRect={picker?.id === msg.id ? picker.rect : null}
            onOpenChange={(v, rect) => setPicker(v ? { id: msg.id, rect } : null)} />
        ) : null}
      </div>
    </div>
    );
  };

  // ─── COMPACT MODE ───
  if (compact) {
    return (
      <div data-testid={`chat-thread-${system}`} className={`rounded-md my-1 ${system === 'client' ? 'bg-[#B5A882]/[0.08] border border-[#B5A882]/20' : 'bg-[#478B89]/[0.08] border border-[#478B89]/20'}`}>
        <button onClick={() => setExpanded(!expanded)} className={`w-full flex items-center justify-between px-4 py-2.5 transition-colors rounded-t-md ${system === 'client' ? 'hover:bg-[#B5A882]/[0.12]' : 'hover:bg-[#478B89]/[0.12]'}`}
          data-testid={`chat-toggle-${system}`}>
          <span className={`font-sans text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5 ${system === 'client' ? 'text-ug-gold' : 'text-oxidized-teal'}`}>
            {systemIcon} {systemLabel}
            {clientNameSuffix && <span className="font-sans text-[9px] font-normal normal-case tracking-normal text-slate-500 truncate max-w-[120px]">· {clientNameSuffix}</span>}
            {/* "PM on duty" chip — editor-only, internal chat only, only when set.
                Display-only echo of the dashboard banner at the moment of tagging.
                Server value source: db.settings.pm_on_duty (see DashboardPage banner). */}
            {system === 'internal' && user.role === 'editor' && pmOnDuty?.name && (
              <span
                data-testid="chat-on-duty-chip"
                className="tap-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-oxidized-teal/[0.15] border border-oxidized-teal/30 font-sans text-[9px] font-medium normal-case tracking-normal text-oxidized-teal max-w-[140px] truncate"
                title={`Project Manager on duty: ${pmOnDuty.name}`}>
                <span className="text-oxidized-teal leading-none" aria-hidden="true">●</span>
                <span className="truncate">On duty: <span className="text-zinc-100 font-semibold">{pmOnDuty.name}</span></span>
              </span>
            )}
            {unreadCount > 0 && <span className="bg-ug-status-red text-white text-[9px] font-bold min-w-[20px] h-5 px-1.5 rounded flex items-center justify-center shadow-[0_0_6px_rgba(239,68,68,0.4)]">{unreadCount}</span>}
          </span>
          {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 tap-auto" /> : <ChevronUp className="w-3.5 h-3.5 text-slate-500 tap-auto" />}
        </button>
        {expanded && (
          <div>
            <div ref={scrollRef} className={`${composerGrow > 0 ? 'min-h-[96px]' : 'min-h-[150px]'} overflow-y-auto px-4 py-3 ${system === 'client' ? 'bg-[#B5A882]/[0.03]' : 'bg-[#478B89]/[0.03]'}`} style={{ height: `calc(100vh - 520px - ${composerGrow}px)` }}>
              <div className="space-y-6">
                {messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
              </div>
              {messages.length === 0 && <p className="text-center font-sans text-[9px] font-semibold text-slate-600 py-6">No messages yet</p>}
            </div>
            {!readOnly && (
              <div
                ref={composerRef}
                className={`px-4 pb-3 pt-2 relative ${dragOver ? 'bg-ug-gold/[0.06] ring-2 ring-ug-gold/40 ring-inset' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid={`chat-dropzone-${system}`}>
                {dragOver && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-ug-gold">Drop images to attach</span>
                  </div>
                )}
                {pendingAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2" data-testid={`chat-pending-attachments-${system}`}>
                    {pendingAttachments.map((a) => (
                      <div key={a.tempId} className="relative w-16 h-16 rounded-md overflow-hidden border border-ug-border bg-ug-surface">
                        <img src={a.previewUrl} alt={a.filename} className="w-full h-full object-cover" />
                        {a.uploading && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="font-mono text-[8px] text-ug-gold uppercase tracking-wider">Uploading</span>
                          </div>
                        )}
                        {a.error && (
                          <button
                            data-testid={`chat-pending-retry-${a.tempId}`}
                            onClick={() => retryAttachment(a.tempId)}
                            title={a.error}
                            className="absolute inset-0 bg-ug-status-red/70 hover:bg-ug-status-red/80 flex flex-col items-center justify-center gap-0.5 transition-colors">
                            <RefreshCw className="w-3.5 h-3.5 text-white" />
                            <span className="font-mono text-[7px] text-white uppercase tracking-wider">Retry</span>
                          </button>
                        )}
                        <button
                          data-testid={`chat-pending-remove-${a.tempId}`}
                          onClick={() => removePendingAttachment(a.tempId)}
                          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 hover:bg-ug-status-red text-white flex items-center justify-center tap-sm z-10">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {renderPendingSteps()}
                {renderSuggestion()}
                <div className="flex gap-2 relative">
                <div className="relative">
                  <button ref={emojiRef} onClick={() => setShowEmoji(!showEmoji)} className="tap-sm w-9 h-9 flex items-center justify-center text-slate-500 hover:text-ug-gold rounded transition-colors shrink-0">
                    <Smile className="w-4 h-4" />
                  </button>
                  {showEmoji && emojiRef.current && ReactDOM.createPortal(
                    <div style={{ position: 'fixed', bottom: `${window.innerHeight - emojiRef.current.getBoundingClientRect().top + 4}px`, right: `${window.innerWidth - emojiRef.current.getBoundingClientRect().right}px`, zIndex: 9999 }}>
                      <EmojiGrid onSelect={(e) => { setNewMsg(prev => prev + e); setShowEmoji(false); }} onClose={() => setShowEmoji(false)} />
                    </div>,
                    document.body
                  )}
                </div>
                <button
                  data-testid={`chat-attach-${system}`}
                  onClick={openFilePicker}
                  title="Attach image"
                  className="tap-sm w-9 h-9 flex items-center justify-center text-slate-500 hover:text-ug-gold rounded transition-colors shrink-0">
                  <Paperclip className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  multiple
                  onChange={onFileInputChange}
                  className="hidden"
                  data-testid={`chat-file-input-${system}`}
                />
                <div className="flex-1 relative">
                  {bigBox ? bigBoxNote : <>
                  {showMentions && taggableUsers.filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch)).length > 0 && (
                    <div className="absolute bottom-10 left-0 w-48 bg-ug-raised border border-ug-border-strong rounded-lg shadow-xl z-50 py-1 max-h-40 overflow-y-auto" data-testid="mention-list">
                      {taggableUsers.filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch)).map(u => (
                        <button key={u.id} onClick={() => insertMention(u)} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-white/[0.05] text-left tap-sm">
                          <span className="text-[11px] text-zinc-100">{u.name}</span>
                          <span className="text-[8px] text-slate-600 font-mono uppercase">{u.role}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea ref={textareaRef} data-testid={`chat-input-${system}`} value={newMsg} onChange={handleInputChange} onSelect={handleCaretMove} onKeyDown={handleKeyDown} onPaste={handlePaste} maxLength={MAX_MSG_LEN}
                    placeholder="Type @ to mention... (Shift+Enter for new line)" className="bg-ug-surface border border-ug-border text-zinc-100 text-[12px] placeholder:text-white/40 rounded-md pl-3 pr-8 py-2 w-full resize-none overflow-y-auto outline-none focus:border-gold-leaf/40" rows={1} style={{ maxHeight: `${GROW_MAX}px` }} />
                  {sizeButton}
                  {newMsg.length >= SOFT_WARN_AT && (
                    <div data-testid={`chat-char-counter-${system}`} className={`absolute bottom-1 right-2 font-mono text-[9px] pointer-events-none ${newMsg.length >= MAX_MSG_LEN ? 'text-ug-status-red' : 'text-slate-500'}`}>
                      {newMsg.length}/{MAX_MSG_LEN}
                    </div>
                  )}
                  </>}
                </div>
                <Button data-testid={`chat-send-${system}`} onClick={handleSend} disabled={sending || uploading} size="sm"
                  className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-9 w-9 p-0 shrink-0 tap-sm">
                  <Send className="w-3.5 h-3.5" />
                </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {renderBigBox()}
        {lightboxFid && (
          <Lightbox
            src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/serve/${lightboxFid}`}
            alt="attachment"
            onClose={() => setLightboxFid(null)}
          />
        )}
      </div>
    );
  }

  // ─── FULL-SIZE MODE ───
  return (
    <div className="flex flex-col h-[calc(100vh-280px)]" data-testid={`chat-thread-${system}`}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5">
        <div className="space-y-7">
          {messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
        </div>
        {messages.length === 0 && <p className="text-center font-sans text-[10px] font-semibold text-slate-600 py-8">Start a conversation</p>}
      </div>
      {!readOnly && (
        <div
          className={`p-4 border-t border-ug-border relative ${dragOver ? 'bg-ug-gold/[0.06] ring-2 ring-ug-gold/40 ring-inset' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid={`chat-dropzone-${system}`}>
          {dragOver && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="font-mono text-[12px] uppercase tracking-wider text-ug-gold">Drop images to attach</span>
            </div>
          )}
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid={`chat-pending-attachments-${system}`}>
              {pendingAttachments.map((a) => (
                <div key={a.tempId} className="relative w-20 h-20 rounded-md overflow-hidden border border-ug-border bg-ug-surface">
                  <img src={a.previewUrl} alt={a.filename} className="w-full h-full object-cover" />
                  {a.uploading && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="font-mono text-[9px] text-ug-gold uppercase tracking-wider">Uploading</span>
                    </div>
                  )}
                  {a.error && (
                    <button
                      data-testid={`chat-pending-retry-${a.tempId}`}
                      onClick={() => retryAttachment(a.tempId)}
                      title={a.error}
                      className="absolute inset-0 bg-ug-status-red/70 hover:bg-ug-status-red/80 flex flex-col items-center justify-center gap-0.5 transition-colors">
                      <RefreshCw className="w-4 h-4 text-white" />
                      <span className="font-mono text-[8px] text-white uppercase tracking-wider">Retry</span>
                    </button>
                  )}
                  <button
                    data-testid={`chat-pending-remove-${a.tempId}`}
                    onClick={() => removePendingAttachment(a.tempId)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-ug-status-red text-white flex items-center justify-center tap-sm z-10">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {renderPendingSteps()}
          {renderSuggestion()}
          <div className="flex gap-2 relative">
          <div className="relative">
            <button ref={emojiRef} onClick={() => setShowEmoji(!showEmoji)} className="tap-sm w-10 h-10 flex items-center justify-center text-slate-500 hover:text-ug-gold rounded transition-colors shrink-0">
              <Smile className="w-5 h-5" />
            </button>
            {showEmoji && emojiRef.current && ReactDOM.createPortal(
              <div style={{ position: 'fixed', bottom: `${window.innerHeight - emojiRef.current.getBoundingClientRect().top + 4}px`, right: `${window.innerWidth - emojiRef.current.getBoundingClientRect().right}px`, zIndex: 9999 }}>
                <EmojiGrid onSelect={(e) => { setNewMsg(prev => prev + e); setShowEmoji(false); }} onClose={() => setShowEmoji(false)} />
              </div>,
              document.body
            )}
          </div>
          <button
            data-testid={`chat-attach-${system}`}
            onClick={openFilePicker}
            title="Attach image"
            className="tap-sm w-10 h-10 flex items-center justify-center text-slate-500 hover:text-ug-gold rounded transition-colors shrink-0">
            <Paperclip className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            onChange={onFileInputChange}
            className="hidden"
            data-testid={`chat-file-input-${system}`}
          />
          <div className="flex-1 relative">
            {bigBox ? bigBoxNote : <>
            {showMentions && taggableUsers.filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch)).length > 0 && (
              <div className="absolute bottom-12 left-0 w-56 bg-ug-raised border border-ug-border-strong rounded-lg shadow-xl z-50 py-1 max-h-48 overflow-y-auto" data-testid="mention-list">
                {taggableUsers.filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch)).map(u => (
                  <button key={u.id} onClick={() => insertMention(u)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.05] text-left tap-sm">
                    <span className="text-[12px] text-zinc-100">{u.name}</span>
                    <span className="text-[9px] text-slate-600 font-mono uppercase">{u.role}</span>
                  </button>
                ))}
              </div>
            )}
            <textarea ref={textareaRef} data-testid={`chat-input-${system}`} value={newMsg} onChange={handleInputChange} onSelect={handleCaretMove} onKeyDown={handleKeyDown} onPaste={handlePaste} maxLength={MAX_MSG_LEN}
              placeholder="Type @ to mention... (Shift+Enter for new line)" className="bg-ug-surface border border-ug-border text-zinc-100 text-sm placeholder:text-white/40 rounded-md pl-3 pr-8 py-2.5 w-full resize-none overflow-y-auto outline-none focus:border-gold-leaf/40" rows={1} style={{ maxHeight: `${GROW_MAX}px` }} />
            {sizeButton}
            {newMsg.length >= SOFT_WARN_AT && (
              <div data-testid={`chat-char-counter-${system}`} className={`absolute bottom-1.5 right-2 font-mono text-[10px] pointer-events-none ${newMsg.length >= MAX_MSG_LEN ? 'text-ug-status-red' : 'text-slate-500'}`}>
                {newMsg.length}/{MAX_MSG_LEN}
              </div>
            )}
            </>}
          </div>
          {(user.role === 'editor') && (
            <button onClick={() => setUrgent(!urgent)} title="Mark as urgent" className={`tap-sm w-10 h-10 flex items-center justify-center rounded transition-colors shrink-0 ${urgent ? 'text-ug-status-red bg-ug-status-red/10' : 'text-slate-600 hover:text-ug-status-red'}`}>
              <span className="text-lg">!</span>
            </button>
          )}
          <Button data-testid={`chat-send-${system}`} onClick={handleSend} disabled={sending || uploading} size="sm"
            className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-10 px-3">
            <Send className="w-4 h-4" />
          </Button>
          </div>
        </div>
      )}
      {renderBigBox()}
      {lightboxFid && (
        <Lightbox
          src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/serve/${lightboxFid}`}
          alt="attachment"
          onClose={() => setLightboxFid(null)}
        />
      )}
    </div>
  );
}
